import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const Pagination = ({ currentPage, totalPages, handlePageChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Only touch the URL when "page" is actually out of sync. setSearchParams()
  // unconditionally calls navigate() the moment it's invoked -- even if the
  // updater function you pass it bails out and returns the params unchanged
  // -- so the old fix (checking *inside* the updater) still fired a
  // navigation every render. And since useSearchParams()'s own setter is
  // re-created whenever the location changes, that navigation produced a new
  // setSearchParams reference, which (being in this effect's deps) re-ran
  // the effect, which navigated again -- an infinite loop that crashed the
  // page with "Maximum update depth exceeded" and froze in-app navigation.
  // The fix is to never call setSearchParams at all once "page" matches, so
  // deliberately reading `searchParams` here without listing it (or
  // setSearchParams) as a dependency -- only `currentPage` should retrigger this.
  useEffect(() => {
    if (searchParams.get("page") === String(currentPage)) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("page", currentPage);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const pagesToShow = 2;
  const minimumPages = Math.floor(pagesToShow / 2);

  const getVisiblePages = () => {
    let start = Math.max(1, currentPage - minimumPages);
    let end = Math.min(totalPages, currentPage + minimumPages);

    if (currentPage - minimumPages < 1) {
      end = Math.min(totalPages, end + (minimumPages - currentPage + 1));
    }
    if (currentPage + minimumPages > totalPages) {
      start = Math.max(1, start - (currentPage + minimumPages - totalPages));
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex flex-row gap-3 p-2">
      {/* Prev Button */}
      <button
        disabled={currentPage === 1}
        onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
        className={`px-2 py-1 ${
          currentPage === 1 ? "cursor-not-allowed text-gray-400" : "hover:text-yellow-500"
        }`}
      >
        Prev
      </button>

      {/* First Page & Dots */}
      {currentPage > minimumPages + 1 && (
        <>
          <button onClick={() => handlePageChange(1)} className="px-2 py-1">
            1
          </button>
          <span>...</span>
        </>
      )}

      {/* Dynamic Page Numbers */}
      {visiblePages.map((page) => (
        <button
          key={page}
          onClick={() => currentPage !== page && handlePageChange(page)}
          className={`px-2 py-1 rounded ${
            currentPage === page ? "bg-yellow-500 text-white" : "hover:bg-gray-200"
          }`}
        >
          {page}
        </button>
      ))}

      {/* Last Page & Dots */}
      {currentPage < totalPages - minimumPages && (
        <>
          <span>...</span>
          <button onClick={() => handlePageChange(totalPages)} className="px-2 py-1">
            {totalPages}
          </button>
        </>
      )}

      {/* Next Button */}
      <button
        disabled={currentPage === totalPages}
        onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
        className={`px-2 py-1 ${
          currentPage === totalPages ? "cursor-not-allowed text-gray-400" : "hover:text-yellow-500"
        }`}
      >
        Next
      </button>
    </div>
  );
};

export default Pagination;
